import { useState, useEffect } from "react";
import { eur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useAccountScope } from "@/context/AccountScopeContext";
import DeleteDemoData from "@/components/DeleteDemoData";
import AccountDialog from "@/components/AccountDialog";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";
import MonthlyReportCard from "@/components/MonthlyReportCard";
import InstallAppCard from "@/components/InstallAppCard";
import { accountTypeMeta } from "@/lib/accounts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bank, PencilSimple, Plus, SignOut, Trash } from "@phosphor-icons/react";

export default function Settings() {
  const { user, logout } = useAuth();
  const { accounts, reloadAccounts } = useAccountScope();
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(null);

  // Balances move with every transaction, so refresh them on arrival.
  const load = () => reloadAccounts().catch(() => {});
  useEffect(() => { reloadAccounts().catch(() => {}); }, [reloadAccounts]);

  const openNewAccount = () => {
    setEditingAccount(null);
    setAccountDialogOpen(true);
  };

  const openEditAccount = (account) => {
    setEditingAccount(account);
    setAccountDialogOpen(true);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tighter">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your profile, accounts, reports and preferences.</p>
      </div>

      {/* Profile */}
      <Card className="bg-card border-border p-5 rounded-lg" data-testid="profile-card">
        <h3 className="font-heading font-bold tracking-tight mb-4">Profile</h3>
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover border border-border" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center font-semibold">
              {(user?.name || "U")[0]}
            </div>
          )}
          <div>
            <p className="font-semibold">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>
      </Card>

      {/* Currency */}
      <Card className="bg-card border-border p-5 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold tracking-tight">Currency</h3>
            <p className="text-sm text-muted-foreground mt-1">Default currency for all values.</p>
          </div>
          <Badge className="bg-secondary text-foreground border-border text-base px-3 py-1">EUR · €</Badge>
        </div>
      </Card>

      {/* Accounts */}
      <Card className="bg-card border-border p-5 rounded-lg" data-testid="accounts-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bank size={18} />
            <h3 className="font-heading font-bold tracking-tight">Accounts</h3>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-border gap-1.5"
            data-testid="add-account-btn"
            onClick={openNewAccount}
          >
            <Plus size={15} /> Add account
          </Button>
        </div>
        <div className="space-y-2">
          {accounts.map((acc) => {
            const meta = accountTypeMeta(acc.type);
            return (
              <div
                key={acc.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-border rounded-md p-3.5"
                data-testid={`account-${acc.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-md bg-secondary flex items-center justify-center">
                    <meta.icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{acc.name}</p>
                      <Badge variant="outline" className="border-border text-[10px] uppercase tracking-wider shrink-0">
                        {meta.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                      Current {eur(acc.current_balance)} · {Number(acc.transaction_count || 0)} transaction{Number(acc.transaction_count || 0) === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 sm:shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="gap-1.5"
                    onClick={() => openEditAccount(acc)}
                    data-testid={`edit-account-${acc.id}`}
                  >
                    <PencilSimple size={15} /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="gap-1.5 text-expense hover:text-expense hover:bg-expense/10"
                    onClick={() => setDeletingAccount(acc)}
                    disabled={accounts.length === 1}
                    title={accounts.length === 1 ? "Add another account before deleting this one" : "Delete account"}
                    data-testid={`delete-account-${acc.id}`}
                  >
                    <Trash size={15} /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Use Main for day-to-day money, Secondary for another bank or card, Savings for money set aside, and Business for company finances.
        </p>
      </Card>

      <AccountDialog
        open={accountDialogOpen}
        onOpenChange={setAccountDialogOpen}
        account={editingAccount}
        onlyAccount={accounts.length === 1}
        onSaved={load}
      />
      <DeleteAccountDialog
        open={Boolean(deletingAccount)}
        onOpenChange={(open) => !open && setDeletingAccount(null)}
        account={deletingAccount}
        accounts={accounts}
        onDeleted={load}
      />

      <MonthlyReportCard />

      <InstallAppCard />

      <DeleteDemoData variant="settings" />

      <Button variant="ghost" onClick={logout} className="text-expense hover:text-expense gap-2" data-testid="settings-logout">
        <SignOut size={16} /> Sign out
      </Button>
    </div>
  );
}
